/**
 * TrackingScripts — injects Meta Pixel, TikTok Pixel, and GA4 scripts
 * based on the current CMS settings (settings.meta_pixel_id, etc.).
 *
 * Usage in App.js:
 *   <SettingsProvider>
 *     <TrackingScripts />
 *     <Routes> ... </Routes>
 *   </SettingsProvider>
 *
 * Also captures UTM/fbclid/ttclid/gclid from URL → localStorage (1h TTL).
 * Exports helper firePixelEvent() used across the app.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "../lib/providers";

const LS_UTM_KEY = "dzts_attrib_v1";
const UTM_TTL_MS = 60 * 60 * 1000; // 1 hour

// --- URL → localStorage attribution capture (runs once on mount) ---
function captureAttribution() {
  try {
    const qs = new URLSearchParams(window.location.search);
    const fields = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "ttclid", "gclid"];
    const captured = {};
    fields.forEach((k) => {
      const v = qs.get(k);
      if (v) captured[k] = v;
    });
    if (Object.keys(captured).length === 0) return;
    const now = Date.now();
    const existing = safeReadAttrib();
    const merged = { ...existing, ...captured, _ts: now };
    localStorage.setItem(LS_UTM_KEY, JSON.stringify(merged));
  } catch {}
}

export function safeReadAttrib() {
  try {
    const raw = localStorage.getItem(LS_UTM_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed?._ts || Date.now() - parsed._ts > UTM_TTL_MS) {
      localStorage.removeItem(LS_UTM_KEY);
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

export function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem("dzts_session_id");
    if (!sid) {
      sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("dzts_session_id", sid);
    }
    return sid;
  } catch {
    return null;
  }
}

// --- Pixel event dispatcher ---
// Meta-only dedup: two call-sites can fire the same event for the same content
// within a few hundred ms (e.g. CartSheet + ThankYou both fire Purchase for the
// same order_id ; ProductPage + ProductDetailSheet both fire ViewContent for
// the same product). We drop the duplicate ONLY for Meta (fbq) using a short
// fingerprint window. TikTok, GA4 and server-side CAPI are untouched.
const __fbqDedup = new Map(); // fingerprint -> timestamp (ms)
const __FBQ_DEDUP_WINDOW_MS = 4000;
function __fbqShouldFire(eventName, params) {
  try {
    const key = params?.order_id
      ? `${eventName}|order:${params.order_id}`
      : params?.content_ids
      ? `${eventName}|cids:${[].concat(params.content_ids).join(",")}`
      : `${eventName}|generic`;
    const now = Date.now();
    const last = __fbqDedup.get(key);
    if (last && now - last < __FBQ_DEDUP_WINDOW_MS) return false;
    __fbqDedup.set(key, now);
    // simple GC to avoid unbounded growth
    if (__fbqDedup.size > 200) {
      for (const [k, t] of __fbqDedup) {
        if (now - t > __FBQ_DEDUP_WINDOW_MS) __fbqDedup.delete(k);
      }
    }
    return true;
  } catch {
    return true;
  }
}

export function firePixelEvent(eventName, params = {}) {
  try {
    if (window.fbq && __fbqShouldFire(eventName, params)) {
      // eventID = numero de commande : Meta fusionne l'achat navigateur et l'achat serveur (CAPI, meme event_id)
      if (params?.order_id) window.fbq("track", eventName, params, { eventID: String(params.order_id) });
      else window.fbq("track", eventName, params);
    }
    if (window.ttq && window.ttq.track) {
      // TikTok exige content_id / contents (Video Shopping Ads) : dérivés de content_ids
      const ids = [].concat(params.content_ids || params.content_id || []).map(String);
      window.ttq.track(eventName, ids.length
        ? { ...params, content_id: ids[0], contents: ids.map((id) => ({ content_id: id, content_type: "product" })) }
        : params);
    }
    if (window.gtag) window.gtag("event", eventName, params);
  } catch {}
}

// --- Dynamic script injection helpers ---
function injectMetaPixel(pixelId) {
  if (!pixelId || window.__fbq_loaded__) return;
  window.__fbq_loaded__ = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0;
    t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", pixelId);
  window.fbq("track", "PageView");
}

function injectTikTokPixel(pixelId) {
  if (!pixelId || window.__ttq_loaded__) return;
  window.__ttq_loaded__ = true;
  /* eslint-disable */
  !(function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = (w[t] = w[t] || []);
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
    ttq.setAndDefer = function (t, e) {
      t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); };
    };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) {
      for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e, n) {
      var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r;
      ttq._t = ttq._t || {}; ttq._t[e] = +new Date(); ttq._o = ttq._o || {}; ttq._o[e] = n || {};
      var o = document.createElement("script");
      o.type = "text/javascript"; o.async = !0; o.src = r + "?sdkid=" + e + "&lib=" + t;
      var a = document.getElementsByTagName("script")[0];
      a.parentNode.insertBefore(o, a);
    };
    ttq.load(pixelId);
    ttq.page();
  })(window, document, "ttq");
  /* eslint-enable */
}

function injectGA4(measurementId) {
  if (!measurementId || window.__ga4_loaded__) return;
  window.__ga4_loaded__ = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", measurementId, { send_page_view: true });
}

// --- React component ---
export function TrackingScripts() {
  const { settings } = useSettings();
  const location = useLocation();

  // Capture attribution params on mount
  useEffect(() => { captureAttribution(); getOrCreateSessionId(); }, []);

  // Fire PageView on every SPA route change (after initial pixel load fires one automatically)
  useEffect(() => {
    if (window.__ga4_loaded__ && window.gtag) {
      window.gtag("event", "page_view", { page_path: location.pathname + location.search });
    }
    if (window.fbq) window.fbq("track", "PageView");
    if (window.ttq && window.ttq.page) window.ttq.page();
  }, [location.pathname, location.search]);

  // Inject pixels when settings load (idempotent via __loaded__ flags)
  useEffect(() => {
    if (!settings) return;
    // Meta Pixel: init as soon as an ID is set (toggle only blocks if explicitly false).
    // This matches the legacy behaviour so existing CMS configs (ID filled, toggle absent) keep firing.
    if (settings.meta_pixel_id && settings.meta_pixel_enabled !== false) {
      injectMetaPixel(settings.meta_pixel_id);
    }
    if (settings.tiktok_pixel_enabled && settings.tiktok_pixel_id) {
      injectTikTokPixel(settings.tiktok_pixel_id);
    }
    if (settings.ga4_enabled && settings.ga4_measurement_id) {
      injectGA4(settings.ga4_measurement_id);
    }
  }, [
    settings?.meta_pixel_enabled, settings?.meta_pixel_id,
    settings?.tiktok_pixel_enabled, settings?.tiktok_pixel_id,
    settings?.ga4_enabled, settings?.ga4_measurement_id,
  ]);

  return null;
}
